import { z } from "zod";

// Common disposable email domains to block
const DISPOSABLE_EMAIL_DOMAINS = [
  "mailinator.com",
  "tempmail.com",
  "guerrillamail.com",
  "10minutemail.com",
  "throwaway.email",
  "fakeinbox.com",
  "trashmail.com",
  "getnada.com",
  "temp-mail.org",
  "dispostable.com",
  "mailnesia.com",
  "sharklasers.com",
  "yopmail.com",
  "maildrop.cc",
  "getairmail.com",
  "mohmal.com",
  "tempail.com",
  "fakemailgenerator.com",
  "emailondeck.com",
  "tempr.email",
  "discard.email",
  "mailcatch.com",
  "mintemail.com",
  "tempinbox.com",
  "spamgourmet.com",
  "mailforspam.com",
  "spam4.me",
  "guerrillamailblock.com",
  "throwawaymail.com",
  "tempmailo.com",
];

const emailSchema = z.string().email("Please enter a valid email address.");

/**
 * Check if local part has 3+ identical consecutive characters (e.g. "aaa", "111")
 */
function hasRepeatedChars(localPart: string): boolean {
  return /(.)\1{2,}/.test(localPart);
}

/**
 * Check if local part is entirely numeric (e.g. "111", "12345")
 */
function isAllNumeric(localPart: string): boolean {
  return /^\d+$/.test(localPart);
}

export function validateEmail(email: string): { valid: boolean; error?: string } {
  // Check format with Zod
  const formatResult = emailSchema.safeParse(email);
  if (!formatResult.success) {
    return { valid: false, error: "Please enter a valid email address." };
  }

  const [localPart, domainPart] = email.split("@");

  // Check minimum local-part length (≥ 2 chars)
  if (!localPart || localPart.length < 2) {
    return { valid: false, error: "Email local part must be at least 2 characters." };
  }

  // Check for disposable email domain
  const domain = domainPart?.toLowerCase();
  if (domain && DISPOSABLE_EMAIL_DOMAINS.includes(domain)) {
    return { valid: false, error: "Please use a non-disposable email address." };
  }

  // Check minimum domain name length (first label before TLD must be ≥ 3 chars)
  if (domain) {
    const domainLabels = domain.split(".");
    if (domainLabels.length >= 2 && domainLabels[0].length < 3) {
      return { valid: false, error: "Email domain name is too short." };
    }
  }

  // Check for repeated consecutive characters (3+)
  if (hasRepeatedChars(localPart)) {
    return { valid: false, error: "Email address appears invalid." };
  }

  // Check for all-numeric local part
  if (isAllNumeric(localPart)) {
    return { valid: false, error: "Email address appears invalid." };
  }

  return { valid: true };
}
